import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { 
  Input, 
  Button, 
  message, 
  Alert,
  Tooltip,
  Spin
} from 'antd';
import { 
  SendOutlined, 
  PlusOutlined, 
  AudioOutlined,
  HistoryOutlined,
  DeleteOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  GlobalOutlined,
  HomeOutlined,
  BulbOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEnhancedChat } from '../../shared/hooks';
import { ChatMessage } from '../../features/chat';
import UserAvatar from '../../components/UserAvatar';
import { useAuth } from '../../contexts/AuthContext';
import FileUploadIntegration from '../../components/FileUploadIntegration';
import { FileUploadItem, ChatFileAttachment } from '../../features/file-upload';
import { VoiceInput } from '../../features/voice-input';
import logo from '../../assets/images/logo.png'; // 修改 logo 导入路径
import { initViewportHeightFix, isMobileDevice, fixMobileLayoutOffset, preventMobileZoom, fixLongTextOverflow } from '../../utils/viewportHeightFix';

const { TextArea } = Input;

// ChatGPT风格的主容器
const ChatContainer = styled.div`
  display: flex;
  height: 100vh;
  width: 100vw;
  background: var(--bg-primary, #ffffff);
  overflow: hidden;
  
  /* 移动端视口高度修复 */
  @media (max-width: 768px) {
    height: 100dvh; /* 使用动态视口高度 */
    min-height: 100vh; /* 降级支持 */
    /* 防止水平滚动和偏移 */
    overflow-x: hidden;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

// 左侧侧边栏
const Sidebar = styled.div<{ $collapsed: boolean }>`
  width: ${props => props.$collapsed ? '0' : '260px'};
  background: var(--bg-secondary, #f7f7f8);
  border-right: 1px solid var(--border-color, #e5e7eb);
  display: flex;
  flex-direction: column;
  transition: width 0.2s ease;
  overflow: hidden;
  
  @media (max-width: 768px) {
    position: fixed;
    left: 0;
    top: 0;
    height: 100vh;
    z-index: 1000;
    width: ${props => props.$collapsed ? '0' : '280px'};
    transform: translateX(${props => props.$collapsed ? '-100%' : '0'});
    box-shadow: ${props => props.$collapsed ? 'none' : '2px 0 8px rgba(0, 0, 0, 0.1)'};
  }
`;

// 侧边栏内容
const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 12px;
  
  @media (max-width: 768px) {
    padding: 8px;
  }
`;

// Logo区域
const LogoSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  user-select: none;
  transition: all 0.3s ease;
  height: 64px;
  position: relative;
  padding: 0 12px;
  margin-bottom: 8px;
  margin-top: -8px;
  gap: 8px;
  
  @media (max-width: 768px) {
    height: 56px;
    padding: 0 8px;
    margin-bottom: 12px;
    margin-top: -4px;
    gap: 6px;
  }
  
  img {
    height: 60px;
    width: 60px;
    object-fit: cover;
    object-position: center;
    transition: transform 0.3s ease;
    transform: scale(1.4);
    position: relative;
    top: 4px;

    @media (max-width: 768px) {
      height: 40px;
      width: 40px;
      transform: scale(1.2);
      top: 2px;
    }

    &:hover {
      transform: scale(1.5);
      
      @media (max-width: 768px) {
        transform: scale(1.3);
      }
    }
  }

  .logo-text {
    font-size: 22px;
    font-weight: 600;
    color: var(--text-primary, #2d3748);
    transition: all 0.3s ease;
    margin-left: 4px;
    white-space: nowrap;
    
    @media (max-width: 768px) {
      font-size: 18px;
      margin-left: 2px;
    }
  }
`;

// 新建聊天按钮
const NewChatButton = styled(Button)`
  &.ant-btn {
    width: 100%;
    height: 44px;
    border-radius: 8px;
    border: 1px solid var(--border-color, #e5e7eb);
    background: transparent;
    color: var(--text-primary, #2d3748);
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    margin-bottom: 16px;
    font-size: 14px;
    
    @media (max-width: 768px) {
      height: 40px;
      margin-bottom: 12px;
      font-size: 13px;
      gap: 6px;
    }
    
    &:hover {
      background: var(--bg-hover, #f3f4f6);
      border-color: var(--border-color, #e5e7eb);
      color: var(--text-primary, #2d3748);
    }
    
    &:focus {
      background: var(--bg-hover, #f3f4f6);
      border-color: var(--primary-color, #10a37f);
      color: var(--text-primary, #2d3748);
    }
  }
`;

// 历史记录区域
const HistoryArea = styled.div`
  flex: 1;
  overflow-y: auto;
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color, #e5e7eb);
    border-radius: 2px;
  }
`;

// 历史记录项
const HistoryItem = styled.div`
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  margin-bottom: 4px;
  transition: background-color 0.2s;
  color: var(--text-primary, #2d3748);
  font-size: 14px;
  
  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 13px;
    margin-bottom: 2px;
    border-radius: 6px;
  }
  
  &:hover {
    background: var(--bg-hover, #f3f4f6);
  }
  
  .history-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

// 侧边栏底部
const SidebarBottom = styled.div`
  border-top: 1px solid var(--border-color, #e5e7eb);
  padding-top: 12px;
  
  @media (max-width: 768px) {
    padding-top: 8px;
  }
`;

// 底部菜单项
const BottomMenuItem = styled.div`
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--text-primary, #2d3748);
  font-size: 14px;
  margin-bottom: 4px;
  transition: background-color 0.2s;
  
  @media (max-width: 768px) {
    padding: 10px 8px;
    font-size: 13px;
    gap: 6px;
    margin-bottom: 2px;
    border-radius: 6px;
  }
  
  &:hover {
    background: var(--bg-hover, #f3f4f6);
  }
`;

// 主内容区域
const MainContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  position: relative;
  
  /* 移动端视口高度修复 */
  @media (max-width: 768px) {
    height: 100dvh; /* 使用动态视口高度 */
    min-height: 100vh; /* 降级支持 */
    /* 确保内容不会偏移 */
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
    /* 防止布局偏移 */
    position: relative;
    left: 0;
    right: 0;
    /* 确保内容居中 */
    margin: 0;
    padding: 0;
  }
`;

// 顶部工具栏
const TopBar = styled.div`
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  border-bottom: 1px solid var(--border-color, #e5e7eb);
  background: var(--bg-primary, #ffffff);
  
  @media (max-width: 768px) {
    height: 56px;
    padding: 0 12px;
    gap: 8px;
  }
`;

// 左侧控制区域
const TopLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

// 切换按钮
const ToggleButton = styled(Button)`
  &.ant-btn {
    width: 36px;
    height: 36px;
    border-radius: 8px;
    border: none;
    background: transparent;
    color: var(--text-primary, #2d3748);
    display: flex;
    align-items: center;
    justify-content: center;
    
    @media (max-width: 768px) {
      width: 40px;
      height: 40px;
      border-radius: 10px;
    }
    
    &:hover {
      background: var(--bg-hover, #f3f4f6);
      color: var(--text-primary, #2d3748);
    }
    
    &:focus {
      background: var(--bg-hover, #f3f4f6);
      color: var(--text-primary, #2d3748);
    }
  }
`;

// 返回首页按钮
const HomeButton = styled(Button)`
  &.ant-btn {
    height: 36px;
    border-radius: 8px;
    border: 1px solid var(--border-color, #e5e7eb);
    background: transparent;
    color: var(--text-primary, #2d3748);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 0 12px;
    font-size: 14px;
    
    @media (max-width: 768px) {
      height: 40px;
      padding: 0 8px;
      font-size: 13px;
      gap: 4px;
      border-radius: 10px;
    }
    
    &:hover {
      background: var(--bg-hover, #f3f4f6);
      border-color: var(--primary-color, #10a37f);
      color: var(--primary-color, #10a37f);
    }
    
    &:focus {
      background: var(--bg-hover, #f3f4f6);
      border-color: var(--primary-color, #10a37f);
      color: var(--primary-color, #10a37f);
    }
  }
`;

// 右侧用户区域
const TopRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

// 用户控制区域
const UserControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

// 语言切换按钮
const LanguageButton = styled(Button)`
  &.ant-btn {
    border: 1px solid var(--border-color, #e5e7eb);
    border-radius: 6px;
    padding: 0 8px;
    height: 32px;
    background: transparent;
    color: var(--text-primary, #2d3748);
    font-size: 12px;
    
    @media (max-width: 768px) {
      height: 36px;
      padding: 0 10px;
      border-radius: 8px;
      font-size: 13px;
      min-width: 40px;
    }
    
    &:hover {
      border-color: var(--primary-color, #10a37f);
      color: var(--primary-color, #10a37f);
      background: transparent;
    }
    
    &:focus {
      border-color: var(--primary-color, #10a37f);
      color: var(--primary-color, #10a37f);
      background: transparent;
    }
  }
`;

// 消息区域
const MessagesArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  scroll-behavior: smooth;
  
  @media (max-width: 768px) {
    padding: 4px 2px;
    align-items: stretch; /* 移动端让子元素占满宽度 */
    /* 为固定输入区域留出空间 */
    padding-bottom: calc(80px + env(safe-area-inset-bottom, 0));
    /* 防止水平滚动和偏移 */
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
    /* 确保内容不会偏移 */
    position: relative;
    left: 0;
    right: 0;
    
    /* 当有三阶段分析状态时，增加底部边距 */
    &.has-phase-status {
      padding-bottom: calc(112px + env(safe-area-inset-bottom, 0)); /* 80px + 32px */
    }
  }
  
  /* 优化滚动性能 */
  will-change: scroll-position;
  transform: translateZ(0); /* 启用硬件加速 */
  
  &::-webkit-scrollbar {
    width: 6px;
    
    @media (max-width: 768px) {
      width: 3px;
    }
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--border-color, #e5e7eb);
    border-radius: 3px;
  }
`;

// 消息容器 - 限制最大宽度，居中显示
const MessagesContainer = styled.div`
  width: 100%;
  max-width: 920px; /* 从768px增加到920px，提供更宽的显示区域 */
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%; /* 确保在移动端占满宽度 */
    padding: 0;
    gap: 12px;
    /* 防止内容偏移 */
    overflow-x: hidden;
    position: relative;
    left: 0;
    right: 0;
    /* 确保内容不会超出屏幕 */
    box-sizing: border-box;
  }
  
  /* 防止布局跳跃的优化 */
  contain: layout style;
  
  /* 流式消息的特殊处理 */
  .streaming-message {
    min-height: 40px; /* 设置最小高度避免跳跃 */
    transition: height 0.2s ease-out;
  }
  
  /* 优化渲染性能 */
  .message-content {
    contain: layout;
    word-wrap: break-word;
    overflow-wrap: break-word;
  }
`;

// 欢迎区域
const WelcomeArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  max-width: 920px; /* 与其他容器保持一致 */
  margin: 0 auto;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 20px 16px;
    justify-content: flex-start;
    margin-top: 20px;
    max-width: 90% !important;
    width: 90% !important;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: var(--text-primary, #2d3748);
  margin-bottom: 16px;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 12px;
    max-width: 100% !important;
    width: 100% !important;
  }
`;

const WelcomeDescription = styled.p`
  font-size: 16px;
  color: var(--text-secondary, #8e8ea0);
  line-height: 1.5;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    font-size: 14px;
    margin-bottom: 24px;
    line-height: 1.4;
    max-width: 100% !important;
    width: 100% !important;
    padding: 0 8px !important;
    box-sizing: border-box !important;
    word-wrap: break-word !important;
    overflow-wrap: break-word !important;
  }
`;

// 示例问题区域
const ExampleQuestions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 16px;
  width: 100%;
  max-width: 600px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    max-width: 90% !important;
    width: 90% !important;
    margin: 0 auto !important;
    padding: 0 16px !important;
    box-sizing: border-box !important;
    overflow: visible !important;
  }
`;

const ExampleCard = styled.div`
  padding: 16px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    padding: 12px 16px !important;
    border-radius: 10px !important;
    border: 1px solid #d1d5db !important;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05) !important;
    width: 100% !important;
    max-width: 100% !important;
    box-sizing: border-box !important;
    overflow: visible !important;
  }
  
  &:hover {
    border-color: #10a37f;
    box-shadow: 0 4px 12px 0 rgba(16, 163, 127, 0.15);
    transform: translateY(-2px);
    
    @media (max-width: 768px) {
      transform: translateY(-1px);
      box-shadow: 0 2px 8px 0 rgba(16, 163, 127, 0.1) !important;
    }
  }
  
  .example-text {
    color: #2d3748;
    font-size: 14px;
    line-height: 1.4;
    text-align: center;
    
    @media (max-width: 768px) {
      font-size: 13px !important;
      text-align: center !important;
      margin: 0 !important;
      padding: 0 !important;
      word-wrap: break-word !important;
      overflow-wrap: break-word !important;
      max-width: 100% !important;
      box-sizing: border-box !important;
    }
  }
`;

// 输入区域
const InputArea = styled.div`
  position: sticky;
  bottom: 0;
  background: var(--bg-primary, #ffffff);
  border-top: 1px solid var(--border-color, #e5e7eb);
  padding: 20px;
  z-index: 10;
  display: flex;
  justify-content: center;
  
  @media (max-width: 768px) {
    padding: 12px 2px;
    /* 移动端使用固定定位确保始终在底部 */
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    /* 考虑安全区域和三阶段分析状态显示 */
    padding-bottom: calc(12px + env(safe-area-inset-bottom, 0));
    
    /* 当有三阶段分析状态时，增加底部边距 */
    &.has-phase-status {
      bottom: 32px; /* 为三阶段分析状态显示留出空间 */
    }
  }
`;

const InputContainer = styled.div`
  width: 100%;
  max-width: 920px; /* 与MessagesContainer保持一致 */
  position: relative;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

// ChatGPT风格的输入框容器
const ChatInputContainer = styled.div`
  background: #f7f7f8;
  border: 1px solid #e5e7eb;
  border-radius: 24px;
  padding: 8px 12px;
  box-shadow: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  transition: all 0.2s ease;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  
  @media (max-width: 768px) {
    border-radius: 20px;
    padding: 6px 10px;
    gap: 6px;
    border: 1px solid #d1d5db;
  }
  
  &:focus-within {
    border-color: #10a37f;
    box-shadow: 0 0 0 2px rgba(16, 163, 127, 0.2);
  }
  
  &:hover {
    border-color: #d1d5db;
  }
`;

// 左侧功能按钮组
const LeftActionButtons = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 2px;
  }
`;

// 输入框包装器
const InputWrapper = styled.div`
  flex: 1;
  min-width: 0;
`;

// 右侧发送按钮区域
const RightActionArea = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

// 功能按钮
const ActionButton = styled(Button)`
  &.ant-btn {
    border: none;
    background: transparent;
    color: var(--text-secondary, #8e8ea0);
    width: 32px;
    height: 32px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      border-radius: 18px;
    }
    
    &:hover {
      background: var(--bg-hover, #f3f4f6);
      color: var(--text-primary, #2d3748);
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    &:focus {
      background: var(--bg-hover, #f3f4f6);
      color: var(--text-primary, #2d3748);
    }
  }
`;

// 发送按钮
const SendButton = styled(Button)`
  &.ant-btn {
    background: var(--primary-color, #10a37f);
    border: none;
    color: white;
    width: 32px;
    height: 32px;
    border-radius: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    padding: 0;
    
    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      border-radius: 18px;
    }
    
    &:hover {
      background: var(--primary-color-hover, #0d8b6f);
      transform: scale(1.05);
    }
    
    &:active {
      transform: scale(0.95);
    }
    
    &:disabled {
      background: var(--border-color, #e5e7eb);
      color: var(--text-muted, #9ca3af);
      transform: none;
      cursor: not-allowed;
    }
    
    &:focus {
      background: var(--primary-color-hover, #0d8b6f);
    }
  }
`;

// 输入框样式
const StyledTextArea = styled(TextArea)`
  &.ant-input {
    border: none;
    background: transparent;
    color: var(--text-primary, #2d3748);
    resize: none;
    padding: 8px 0;
    font-size: 16px;
    line-height: 1.5;
    
    @media (max-width: 768px) {
      padding: 6px 0;
      font-size: 14px; /* 移动端使用更小的字体 */
    }
    
    &:focus {
      box-shadow: none;
      border: none;
    }
    
    &::placeholder {
      color: var(--text-secondary, #8e8ea0);
      
      @media (max-width: 768px) {
        font-size: 14px; /* 移动端placeholder也使用更小的字体 */
      }
    }
  }
`;

// 移动端遮罩
const MobileMask = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.$visible ? 'block' : 'none'};
  
  @media (min-width: 769px) {
    display: none;
  }
`;



const ChatPageNew: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { state } = useAuth();
  
  // 强制重新渲染的hook
  const [, forceUpdate] = React.useReducer(x => x + 1, 0);
  
  // 监听语言变化
  useEffect(() => {
    // 检查语言是否同步
    const storedLang = localStorage.getItem('preferred_language');
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
    }
    
    const handleLanguageChanged = () => {
      // 强制重新渲染
      forceUpdate();
    };
    
    i18n.on('languageChanged', handleLanguageChanged);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, [i18n]);
  
  // 状态管理
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [currentFiles, setCurrentFiles] = useState<FileUploadItem[]>([]);
  
  // 视口高度修复
  useEffect(() => {
    const cleanup = initViewportHeightFix();
    return cleanup;
  }, []);
  
  // 使用增强的聊天hook
  const {
    messages,
    conversation,
    isLoading,
    isStreaming,
    currentStatus,
    error,
    sendMessage,
    confirmCaseInfo,
    forceStartAnalysis,
    clearError,
    // 聊天历史相关
    chatList,
    currentChatId,
    newChat,
    loadChat,
    deleteChat,
    // 状态重置
    resetLoadingState,
    // 策略选择相关
    handleStrategySelection,
    handleModificationDecision,
    handleReselectStrategy,
  } = useEnhancedChat();
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastScrollTimeRef = useRef<number>(0);

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768 || isMobileDevice();
      setIsMobile(mobile);
      if (mobile) {
        setSidebarCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 确保组件初始化时重置加载状态
  useEffect(() => {
    // 延迟重置，避免与其他初始化逻辑冲突
    const timer = setTimeout(() => {
              if (isLoading && messages.length === 0) {
          resetLoadingState();
        }
    }, 2000);
    
    return () => clearTimeout(timer);
  }, [isLoading, messages.length, resetLoadingState]);

  // 组件挂载时强制重置加载状态（额外的安全措施）
  useEffect(() => {
    // 组件挂载后立即检查状态
    const immediateCheck = setTimeout(() => {
              if (isLoading && messages.length === 0) {
          resetLoadingState();
        }
    }, 500);

    // 延迟再次检查
    const delayedCheck = setTimeout(() => {
              if (isLoading && messages.length === 0) {
          resetLoadingState();
        }
    }, 3000);

    return () => {
      clearTimeout(immediateCheck);
      clearTimeout(delayedCheck);
    };
  }, [isLoading, messages.length, resetLoadingState]); // 只在组件挂载时执行一次

  // 防抖的滚动函数
  const debouncedScrollToBottom = useCallback((force: boolean = false, delay: number = 100) => {
    const now = Date.now();
    
    // 清除之前的定时器
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    
    // 如果是强制滚动或者距离上次滚动超过阈值，立即执行
    if (force || now - lastScrollTimeRef.current > 500) {
      lastScrollTimeRef.current = now;
      
      if (!messagesEndRef.current) return;
      
      const container = messagesEndRef.current.parentElement;
      if (!container) return;
      
      const isNearBottom = force || 
        (container.scrollHeight - container.scrollTop - container.clientHeight) < 50;
      
      if (isNearBottom) {
        requestAnimationFrame(() => {
          messagesEndRef.current?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'end'
          });
        });
      }
    } else {
      // 否则延迟执行
      scrollTimeoutRef.current = setTimeout(() => {
        lastScrollTimeRef.current = now;
        
        if (!messagesEndRef.current) return;
        
        const container = messagesEndRef.current.parentElement;
        if (!container) return;
        
        const isNearBottom = force || 
          (container.scrollHeight - container.scrollTop - container.clientHeight) < 50;
        
        if (isNearBottom) {
          requestAnimationFrame(() => {
            messagesEndRef.current?.scrollIntoView({ 
              behavior: 'smooth',
              block: 'end'
            });
          });
        }
      }, delay);
    }
  }, []);

  // 兼容性的滚动函数
  const scrollToBottom = useCallback((force: boolean = false) => {
    debouncedScrollToBottom(force, 100);
  }, [debouncedScrollToBottom]);

  // 优化的消息监听效果
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      scrollToBottom(true);
    }, 100); // 延迟100ms，等待内容渲染完成

    return () => clearTimeout(timeoutId);
  }, [messages.length, scrollToBottom]); // 只在消息数量变化时滚动

  // 流式内容更新时的智能滚动
  useEffect(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (lastMessage?.isStreaming) {
        // 流式消息更新时，使用更长的延迟和防抖
        debouncedScrollToBottom(false, 500); // 500ms延迟，减少抖动
      }
    }
  }, [messages, debouncedScrollToBottom]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, []);

  // 切换侧边栏
  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // 关闭移动端侧边栏
  const closeMobileSidebar = () => {
    if (isMobile) {
      setSidebarCollapsed(true);
    }
  };

  // 新建聊天
  const handleNewChat = () => {
    newChat();
    setInputMessage('');
    setCurrentFiles([]); // 清空文件列表
    closeMobileSidebar();
  };

  // 加载聊天
  const handleLoadChat = (chatId: string) => {
    loadChat(chatId);
    setInputMessage('');
    setCurrentFiles([]); // 清空文件列表
    closeMobileSidebar();
  };

  // 发送消息
  const handleSendMessage = async () => {
    if ((!inputMessage.trim() && currentFiles.length === 0) || shouldDisableInput) return;

    let messageToSend = inputMessage.trim();
    
    // 如果有文件，添加文件信息到消息中
    if (currentFiles.length > 0) {
      const fileMessage = `${t('chat.fileUploadedMessage')}\n${currentFiles.map(f => `📎 ${f.file.name} (${(f.file.size / 1024 / 1024).toFixed(2)}MB)`).join('\n')}`;

      messageToSend = messageToSend 
        ? `${messageToSend}\n\n${fileMessage}`
        : fileMessage;
    }
    
    setInputMessage('');
    setCurrentFiles([]); // 清空文件列表
    
    try {
      await sendMessage(messageToSend);
    } catch (error) {
      message.error('发送消息失败，请重试');
    }
  };

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 处理输入框焦点，确保加载状态正确
  const handleInputFocus = useCallback(() => {
    // 如果没有消息但是isLoading为true，说明状态有问题，重置它
    if (isLoading && messages.length === 0) {
      resetLoadingState();
    }
  }, [isLoading, messages.length, resetLoadingState]);

  // 处理输入框点击事件（额外的安全措施）
  const handleInputClick = useCallback(() => {
    // 如果输入框因为异常的isLoading状态被禁用，主动重置
    if (isLoading && messages.length === 0) {
      resetLoadingState();
    }
  }, [isLoading, messages.length, resetLoadingState]);

  // 处理示例问题点击
  const handleExampleClick = (example: string) => {
    setInputMessage(example);
    inputRef.current?.focus();
  };

  // 文件上传完成处理
  const handleFilesUploaded = useCallback(async (files: FileUploadItem[], results: any[]) => {
    // 文件上传成功后立即清空显示，不在输入框上方继续显示
    setCurrentFiles([]);
    
    // 重要：处理多模态API结果，启动澄清流程
    if (results && results.length > 0) {
      for (const result of results) {
        // 如果有文件信息，显示文件处理结果
        if (result.file_info) {
          const content = `📎 **${t('chat.fileProcessingComplete')}：${result.file_info.filename}**\n\n**${t('chat.extractedContent')}：**\n${result.file_info.extracted_content || t('chat.fileUploadError')}`;
          
          // 使用sendMessage添加文件信息消息
          await sendMessage(content);
        }
        
        // 如果有澄清数据且需要澄清，显示澄清消息
        if (result.clarification && result.clarification.needs_clarification) {
          const clarificationMessage = result.clarification.clarification_question || t('chat.defaultClarificationMessage');
          
          // 使用sendMessage添加澄清消息
          await sendMessage(clarificationMessage);
        }
      }
    }
    
    // 聚焦到输入框
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, [sendMessage, t]);

  // 移除文件处理
  const handleRemoveFile = useCallback((fileId: string) => {
    setCurrentFiles(prevFiles => prevFiles.filter(file => file.id !== fileId));
  }, []);

  // 语音输入处理
  const handleVoiceTranscript = useCallback((transcript: string) => {
    // 将语音转录结果设置到输入框
    setInputMessage(transcript);
    
    // 聚焦输入框
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  // 语音音频处理
  const handleVoiceAudio = useCallback((audioBlob: Blob) => {
    // 这里可以处理音频文件，比如上传到服务器或进行进一步处理
    // 目前主要使用转录文本，音频文件可选处理
  }, []);

  // 语言切换
  const handleLanguageChange = () => {
    const newLang = i18n.language === 'ja' ? 'zh' : 'ja';
    i18n.changeLanguage(newLang);
    localStorage.setItem('preferred_language', newLang);
  };

  // 智能的输入框禁用逻辑
  const shouldDisableInput = useMemo(() => {
    // 只在真正流式传输时禁用输入
    if (isStreaming) {
      // console.log('正在流式传输，禁用输入');
      return true;
    }
    
    // 在clarifying和confirmed状态下，允许输入（等待用户输入）
    if (conversation.state === 'clarifying' || conversation.state === 'confirmed') {
      // console.log('等待用户输入状态，允许输入');
      return false;
    }
    
    // 只在有消息且正在加载时禁用（避免异常状态）
    if (isLoading && messages.length > 0) {
      // console.log('正在加载且有消息，禁用输入');
      return true;
    }
    
    // 其他情况都允许输入
    return false;
  }, [isLoading, isStreaming, messages.length, conversation.state]);

  // 示例问题
  const exampleQuestions = [
    t('chat.examples.employment'),
    t('chat.examples.traffic'),
    t('chat.examples.contract'),
    t('chat.examples.marriage'),
    t('chat.examples.inheritance'),
    t('chat.examples.debt'),
  ];

  return (
    <ChatContainer className="chat-container">
      {/* 移动端遮罩 */}
      <MobileMask $visible={isMobile && !sidebarCollapsed} onClick={closeMobileSidebar} />
      
      {/* 左侧侧边栏 */}
      <Sidebar $collapsed={sidebarCollapsed}>
        <SidebarContent>
          {/* Logo区域 */}
          <LogoSection onClick={() => navigate('/')}>
            <img src={logo} alt="AtoAI Logo" />
            <span className="logo-text">AtoAI</span>
          </LogoSection>
          
          {/* 新建聊天按钮 */}
          <NewChatButton onClick={handleNewChat}>
            <PlusOutlined />
            {t('chat.newChat')}
          </NewChatButton>
          
          {/* 历史记录区域 */}
          <HistoryArea>
            <div style={{ 
              color: 'var(--text-secondary, #8e8ea0)', 
              fontSize: isMobile ? '11px' : '12px', 
              marginBottom: '8px',
              fontWeight: '500',
              textTransform: 'uppercase',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span>{t('chat.chatHistory')}</span>
              {/* 同步状态提示暂时注释掉
              {isSyncing && (
                <Spin size="small" />
              )}
              */}
            </div>
            
            {/* 真实聊天历史 */}
            {chatList.length === 0 ? (
              <div style={{ 
                color: 'var(--text-secondary, #8e8ea0)', 
                fontSize: isMobile ? '11px' : '12px', 
                textAlign: 'center',
                padding: isMobile ? '16px 8px' : '20px 12px',
                fontStyle: 'italic'
              }}>
                {t('chat.noHistory')}
              </div>
            ) : (
              chatList.map((chat) => (
                <HistoryItem 
                  key={chat.id}
                  onClick={() => handleLoadChat(chat.id)}
                  style={{
                    backgroundColor: currentChatId === chat.id ? 'var(--bg-hover, #f3f4f6)' : 'transparent',
                    borderLeft: currentChatId === chat.id ? '3px solid var(--primary-color, #10a37f)' : '3px solid transparent',
                    position: 'relative',
                    cursor: 'pointer'
                  }}
                >
                  <div className="history-title" style={{ 
                    paddingRight: '24px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {chat.title}
                  </div>
                  <div style={{
                    fontSize: isMobile ? '9px' : '10px',
                    color: 'var(--text-secondary, #8e8ea0)',
                    marginTop: '2px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <span>{new Date(chat.timestamp).toLocaleDateString()}</span>
                    {chat.sessionId && (
                      <span style={{ color: 'var(--success-color, #10a37f)', fontSize: isMobile ? '7px' : '8px' }}>
                        ● {t('chat.synced')}
                      </span>
                    )}
                  </div>
                  {currentChatId !== chat.id && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteChat(chat.id);
                      }}
                      style={{
                        position: 'absolute',
                        top: isMobile ? '10px' : '8px',
                        right: isMobile ? '6px' : '8px',
                        background: 'none',
                        border: 'none',
                        color: 'var(--text-secondary, #8e8ea0)',
                        cursor: 'pointer',
                        fontSize: isMobile ? '14px' : '12px',
                        opacity: 0.5,
                        padding: '2px',
                        transition: 'all 0.2s'
                      }}
                      onMouseOver={(e) => {
                        const target = e.currentTarget;
                        target.style.opacity = '1';
                        target.style.color = 'var(--error-color, #ef4444)';
                      }}
                      onMouseOut={(e) => {
                        const target = e.currentTarget;
                        target.style.opacity = '0.5';
                        target.style.color = 'var(--text-secondary, #8e8ea0)';
                      }}
                    >
                      <DeleteOutlined />
                    </button>
                  )}
                </HistoryItem>
              ))
            )}
          </HistoryArea>
          
          {/* 底部菜单 */}
          <SidebarBottom>
            <BottomMenuItem onClick={() => navigate('/history')}>
              <HistoryOutlined />
              {t('navigation.history')}
            </BottomMenuItem>
            <BottomMenuItem onClick={() => navigate('/settings')}>
              <SettingOutlined />
              {t('navigation.settings')}
            </BottomMenuItem>
          </SidebarBottom>
        </SidebarContent>
      </Sidebar>

      {/* 主内容区域 */}
      <MainContent>
        {/* 顶部工具栏 */}
        <TopBar>
          <TopLeft>
            <ToggleButton onClick={toggleSidebar}>
              {sidebarCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            </ToggleButton>
            <Tooltip title={t('chat.home')}>
              <HomeButton onClick={() => navigate('/')}>
                <HomeOutlined />
                {!isMobile && t('common.home')}
              </HomeButton>
            </Tooltip>
          </TopLeft>

          <TopRight>
            <UserControls>
              {/* 语言切换 */}
              <Tooltip title={i18n.language === 'ja' ? t('navigation.switchToChinese') : t('navigation.switchToJapanese')}>
                <LanguageButton
                  type="text"
                  icon={<GlobalOutlined />}
                  onClick={handleLanguageChange}
                >
                  {i18n.language === 'ja' ? t('chat.languageToggle.showChinese') : t('chat.languageToggle.showJapanese')}
                </LanguageButton>
              </Tooltip>

              {/* 用户菜单 */}
              <UserAvatar onNavigate={isMobile ? closeMobileSidebar : undefined} />
            </UserControls>
          </TopRight>
        </TopBar>

        {/* 开发环境调试信息 */}
        {/* 调试信息已移除 */}

        {/* 消息区域 */}
        {messages.length === 0 ? (
          <WelcomeArea>
                      <WelcomeTitle>{t('chat.welcome')}</WelcomeTitle>
          <WelcomeDescription>
            {t('chat.welcomeDescription')}
          </WelcomeDescription>
            
            <ExampleQuestions>
              {exampleQuestions.map((example, index) => (
                <ExampleCard key={index} onClick={() => handleExampleClick(example)}>
                  <div className="example-text">{example}</div>
                </ExampleCard>
              ))}
            </ExampleQuestions>
          </WelcomeArea>
        ) : (
          <MessagesArea className="chat-messages-area">
            <MessagesContainer>
              {messages.map((msg) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  onConfirmCaseInfo={confirmCaseInfo}
                  onForceStartAnalysis={forceStartAnalysis}
                  onModifyCaseInfo={() => {
                    // 处理修改案件信息
                  }}
                  onStrategySelect={handleStrategySelection}
                  onModificationDecision={handleModificationDecision}
                  onReselectStrategy={handleReselectStrategy}
                />
              ))}
              
            </MessagesContainer>
            <div ref={messagesEndRef} />
          </MessagesArea>
        )}

        {/* 错误提示 */}
        {error && (
          <Alert
            message={t('common.error')}
            description={error}
            type="error"
            closable
            style={{ margin: isMobile ? '8px' : '16px' }}
          />
        )}



        {/* 状态显示3: 输入区域之前的状态显示 */}
        {(currentStatus || isStreaming) && (
          <div style={{
            padding: '8px 20px',
            backgroundColor: 'var(--bg-secondary, #f7f7f8)',
            borderTop: '1px solid var(--border-color, #e5e7eb)',
            textAlign: 'center',
            fontSize: '14px',
            color: 'var(--text-secondary, #8e8ea0)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {isStreaming && <Spin size="small" />}
            <span style={{ fontWeight: '500' }}>
              {currentStatus || t('chat.thinking')}
            </span>
          </div>
        )}

        {/* 输入区域 */}
        <InputArea className="chat-input-area">
          <InputContainer>
            {/* 文件附件显示 */}
            {currentFiles.length > 0 && (
              <ChatFileAttachment
                files={currentFiles}
                onRemove={handleRemoveFile}
                compact={isMobile}
              />
            )}
            
            <ChatInputContainer>
              <LeftActionButtons>
                {/* 文件上传 */}
                <FileUploadIntegration
                  onFilesUploaded={handleFilesUploaded}
                  disabled={shouldDisableInput}
                  maxFiles={5}
                  maxFileSize={10 * 1024 * 1024}
                  userQuery={inputMessage.trim() || t('chat.fileAnalysisDefaultQuery')}
                  userId={state.user?.id?.toString() || 'anonymous'}
                  sessionId={conversation?.conversationId || undefined}
                />

                {/* 语音输入 */}
                <VoiceInput
                  onTranscriptReceived={handleVoiceTranscript}
                  onAudioReceived={handleVoiceAudio}
                  disabled={shouldDisableInput}
                  maxDuration={60}
                  language={i18n.language} // 明确传递当前界面语言
                />
              </LeftActionButtons>

              <InputWrapper>
                <StyledTextArea
                  ref={inputRef}
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  onFocus={handleInputFocus}
                  onClick={handleInputClick}
                  placeholder={t('chat.inputPlaceholder')}
                  autoSize={{ minRows: 1, maxRows: isMobile ? 4 : 6 }}
                  disabled={shouldDisableInput}
                />
              </InputWrapper>

              <RightActionArea>
                <SendButton
                  onClick={handleSendMessage}
                  disabled={(!inputMessage.trim() && currentFiles.length === 0) || shouldDisableInput}
                >
                  <SendOutlined />
                </SendButton>
              </RightActionArea>
            </ChatInputContainer>
          </InputContainer>
        </InputArea>
      </MainContent>
    </ChatContainer>
  );
};

export default ChatPageNew; 